import { externalServices } from 'platform/monitoring/DowntimeNotification';
import environment from 'platform/utilities/environment';
import FormFooter from 'platform/forms/components/FormFooter';
import { VA_FORM_IDS } from 'platform/forms/constants';
import { blankSchema } from 'platform/forms-system/src/js/utilities/data/profile';
import { defaultItemPageScrollAndFocusTarget as scrollAndFocusTarget } from 'platform/forms-system/src/js/patterns/array-builder';
import { TITLE, SUBTITLE } from '../utils/constants';
import manifest from '../manifest.json';
import content from '../utils/content.json';
import IntroductionPage from '../containers/IntroductionPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import FormSavedPage from '../containers/FormSavedPage';
import { defaultDefinitions } from './definitions';
import GetFormHelp from '../components/GetFormHelp';
import ErrorText from '../components/ErrorText';
import * as veteranInformation from './chapters/01-veteran-information';
import * as claimantInformation from './chapters/02-claimant-information';
import * as militaryHistory from './chapters/03-military-history';
import * as householdInformation from './chapters/04-household-information';
import * as claimInformation from './chapters/05-claim-information';
import * as financialInformation from './chapters/06-financial-information';
import * as additionalInformation from './chapters/07-additional-information';
import {
  getCaveArtifactValidationErrors,
  hasVeteranInfoConflicts,
  hasMilitaryHistoryConflicts,
  caveUploadDocumentsPage,
  caveRequiredDocumentsPage,
  caveUploadRequiredDocumentsPage,
  caveConfirmVeteranInfoPage,
  caveConfirmMilitaryHistoryPage,
  ArtifactReviewPage,
} from '../cave';
import IncorrectForm from '../containers/IncorrectForm';
import { transform } from './submit-transformer';
import prefillTransformer from './prefill-transformer';
import migrations from './migrations';
import { applyForceDivWrapperToFormConfig } from './uiSchemaHelpers';
import {
  focusH3,
  doAnyChildrenNotLiveWith,
  isRelationshipToVeteran,
} from '../utils/helpers';
import ReviewPageIntroduction from '../components/ReviewPageIntroduction';

/** @type {FormConfig} */
const formConfig = {
  rootUrl: manifest.rootUrl,
  urlPrefix: '/',
  submitUrl: `${environment.API_URL}/survivors_benefits/v0/form534ez`,
  transformForSubmit: transform,
  trackingPrefix: 'survivors-534ez',
  useCustomScrollAndFocus: true,
  scrollAndFocusTarget: focusH3, // scroll and focus fallback
  v3SegmentedProgressBar: true,
  CustomReviewTopContent: ReviewPageIntroduction,
  prefillEnabled: true,
  prefillTransformer,
  dev: {
    disableWindowUnloadInCI: true,
    showNavLinks: true,
    collapsibleNavLinks: true,
  },
  downtime: {
    dependencies: [externalServices.icmhs],
  },
  formId: VA_FORM_IDS.FORM_21P_534EZ,
  saveInProgress: {
    messages: {
      inProgress: content['config-sip-in-progress'],
      expired: content['config-sip-in-expired'],
      saved: content['config-sip-saved'],
    },
  },
  version: migrations.length,
  formSavedPage: FormSavedPage,
  defaultDefinitions,
  customValidationErrors: getCaveArtifactValidationErrors,
  savedFormMessages: {
    notFound: content['config-saved-not-found'],
    noAuth: content['config-saved-no-auth'],
  },
  preSubmitInfo: {
    statementOfTruth: {
      body: content['config-pre-submit-body'],
      messageAriaDescribedby: content['config-pre-submit-aria'],
      useProfileFullName: true,
      fullNamePath: 'yourName',
    },
  },
  title: TITLE,
  subTitle: SUBTITLE,
  introduction: IntroductionPage,
  migrations,
  confirmation: ConfirmationPage,
  footerContent: FormFooter,
  getHelp: GetFormHelp,
  errorText: ErrorText,
  showReviewErrors: !environment.isProduction(),
  chapters: {
    // Relationship to Veteran
    yourIdentity: {
      title: content['your-identity-chapter-title'],
      pages: {
        claimantRelationship: {
          path: 'claimant-relationship',
          title: content['claimant-relationship-title'],
          uiSchema: claimantInformation.claimantRelationship.uiSchema,
          schema: claimantInformation.claimantRelationship.schema,
        },
        claimantOther: {
          path: 'claimant-other',
          title: content['claimant-other-title'],
          CustomPage: IncorrectForm,
          CustomPageReview: null,
          uiSchema: {},
          schema: blankSchema,
          depends: formData => formData.claimantRelationship === 'OTHER',
        },
      },
    },
    // Required Documents
    requiredDocumentUpload: {
      title: "Veteran's DD214 and death certificate",
      hideOnReviewPage: true,
      pages: {
        requiredDocuments: {
          title: "Veteran's DD214 and death certificate",
          path: 'required-documents/overview',
          depends: formData => formData?.['view:idpEnabled'] === true,
          uiSchema: caveRequiredDocumentsPage.uiSchema,
          schema: caveRequiredDocumentsPage.schema,
        },
        uploadRequiredDocuments: {
          title: 'Upload DD214 and death certificate',
          path: 'required-documents/upload',
          depends: formData => formData?.['view:idpEnabled'] === true,
          uiSchema: caveUploadRequiredDocumentsPage.uiSchema,
          schema: caveUploadRequiredDocumentsPage.schema,
        },
      },
    },
    // Claimant Information
    claimantInformation: {
      title: ({ formData }) =>
        isRelationshipToVeteran(formData) === 'custodian'
          ? content['claimant-information-chapter-title-custodian']
          : content['claimant-information-chapter-title'],
      pages: {
        ...claimantInformation.claimantPersonalInformation,
        ...claimantInformation.claimantContactInformation,
        // TODO(phase 2): remove the legacy yourInformation/yourIdentification
        // pages after survivors_benefits_updated_prefill is 100% in prod
        yourInformation: {
          path: 'your-information',
          title: ({ formData }) =>
            claimantInformation.yourInformationTitle(formData),
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true,
          uiSchema: claimantInformation.yourInformation.uiSchema,
          schema: claimantInformation.yourInformation.schema,
        },
        yourIdentification: {
          path: 'your-identification',
          title: content['your-id-title'],
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true &&
            isRelationshipToVeteran(formData) !== 'custodian',
          uiSchema: claimantInformation.yourIdentification.uiSchema,
          schema: claimantInformation.yourIdentification.schema,
        },
        // TODO(phase 2): remove the legacy yourContact/yourAddress pages after
        // survivors_benefits_updated_prefill is 100% in prod
        yourAddress: {
          path: 'your-address',
          title: content['your-address-title'],
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true,
          uiSchema: claimantInformation.yourAddress.uiSchema,
          schema: claimantInformation.yourAddress.schema,
        },
        yourContact: {
          path: 'your-contact',
          title: content['your-contact-title'],
          depends: formData =>
            formData?.survivorsBenefitsUpdatedPrefill !== true,
          uiSchema: claimantInformation.yourContact.uiSchema,
          schema: claimantInformation.yourContact.schema,
        },
        childRelationship: {
          path: 'child-relationship',
          title: content['child-relationship-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childRelationship.uiSchema,
          schema: claimantInformation.childRelationship.schema,
        },
        yourHistory: {
          path: 'your-service-history',
          title: content['your-history-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) !== 'custodian',
          uiSchema: claimantInformation.yourHistory.uiSchema,
          schema: claimantInformation.yourHistory.schema,
        },
        childInformation: {
          path: 'child-information',
          title: content['child-info-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childInformation.uiSchema,
          schema: claimantInformation.childInformation.schema,
        },
        childIdentification: {
          path: 'child-identification',
          title: content['child-id-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childIdentification.uiSchema,
          schema: claimantInformation.childIdentification.schema,
        },
        childAddress: {
          path: 'child-address',
          title: content['child-address-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childAddress.uiSchema,
          schema: claimantInformation.childAddress.schema,
        },
        childContact: {
          path: 'child-contact',
          title: content['child-contact-title'],
          depends: formData =>
            isRelationshipToVeteran(formData) === 'custodian',
          uiSchema: claimantInformation.childContact.uiSchema,
          schema: claimantInformation.childContact.schema,
        },
        benefitType: {
          path: 'claimant-benefit-type',
          title: content['benefit-type-title'],
          uiSchema: claimantInformation.benefitType.uiSchema,
          schema: claimantInformation.benefitType.schema,
        },
      },
    },
    // Veteran Information
    veteranInformation: {
      title: 'Veteran’s information',
      pages: {
        veteranName: {
          title: content['veteran-name-title'],
          path: 'veteran',
          uiSchema: veteranInformation.veteranName.uiSchema,
          schema: veteranInformation.veteranName.schema,
        },
        veteranIdentification: {
          title: content['veteran-id-title'],
          path: 'veteran-identification',
          uiSchema: veteranInformation.veteranIdentification.uiSchema,
          schema: veteranInformation.veteranIdentification.schema,
        },
        veteranIdentificationAdditional: {
          title: content['veteran-additional-title'],
          path: 'veteran-additional-information',
          uiSchema: veteranInformation.veteranAdditional.uiSchema,
          schema: veteranInformation.veteranAdditional.schema,
        },
      },
    },
    // Military History
    militaryHistory: {
      title: content['military-history-chapter-title'],
      pages: {
        militaryHistory: {
          path: 'va-benefits',
          title: content['military-va-benefits-title'],
          uiSchema: militaryHistory.vaBenefits.uiSchema,
          schema: militaryHistory.vaBenefits.schema,
        },
        servicePeriod: {
          path: 'service-period',
          title: content['military-service-period-title'],
          depends: formData => formData.receivedBenefits === false,
          uiSchema: militaryHistory.servicePeriod.uiSchema,
          schema: militaryHistory.servicePeriod.schema,
        },
        nationalGuardService: {
          path: 'national-guard-service',
          title: content['military-service-national-guard-title'],
          depends: formData => formData.receivedBenefits === false,
          uiSchema: militaryHistory.nationalGuardService.uiSchema,
          schema: militaryHistory.nationalGuardService.schema,
        },
        nationalGuardServicePeriod: {
          path: 'national-guard-service-period',
          title: content['military-service-national-guard-service-title'],
          depends: formData =>
            formData.receivedBenefits === false &&
            formData.nationalGuardActivated === true,
          uiSchema: militaryHistory.nationalGuardServicePeriod.uiSchema,
          schema: militaryHistory.nationalGuardServicePeriod.schema,
        },
        nationalGuardUnitAddress: {
          path: 'national-guard-unit-address',
          title: content['military-service-national-guard-unit-address-title'],
          depends: formData =>
            formData.receivedBenefits === false &&
            formData.nationalGuardActivated === true,
          uiSchema: militaryHistory.nationalGuardUnitAddress.uiSchema,
          schema: militaryHistory.nationalGuardUnitAddress.schema,
        },
        ...militaryHistory.otherServiceNamesPages,
        prisonerOfWar: {
          path: 'prisoner-of-war',
          title: content['military-service-pow-title'],
          depends: formData => formData.receivedBenefits === false,
          uiSchema: militaryHistory.prisonerOfWarPage.uiSchema,
          schema: militaryHistory.prisonerOfWarPage.schema,
        },
        powPeriodOfTime: {
          path: 'prisoner-of-war-period',
          title: content['military-service-pow-period-title'],
          depends: formData =>
            formData.receivedBenefits === false && formData?.pow === true,
          uiSchema: militaryHistory.powPeriodOfTimePage.uiSchema,
          schema: militaryHistory.powPeriodOfTimePage.schema,
        },
      },
    },
    // Household Information
    householdInformation: {
      title: content['household-chapter-title'],
      pages: {
        marriageToVeteran: {
          path: 'household/marriage-to-veteran',
          title: content['household-veteran-marriage-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageToVeteran.uiSchema,
          schema: householdInformation.marriageToVeteran.schema,
        },
        marriageToVeteranLocation: {
          path: 'household/marriage-to-veteran-location',
          title: content['household-veteran-marriage-location-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageToVeteranLocation.uiSchema,
          schema: householdInformation.marriageToVeteranLocation.schema,
        },
        marriageToVeteranInfo: {
          path: 'household/marriage-to-veteran-info',
          title: content['household-veteran-marriage-info-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageToVeteranInfo.uiSchema,
          schema: householdInformation.marriageToVeteranInfo.schema,
          scrollAndFocusTarget,
        },
        marriageToVeteranEnd: {
          path: 'household/marriage-to-veteran-end',
          title: content['household-veteran-marriage-end-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            !formData.marriedToVeteranAtTimeOfDeath,
          uiSchema: householdInformation.marriageToVeteranEnd.uiSchema,
          schema: householdInformation.marriageToVeteranEnd.schema,
          scrollAndFocusTarget,
        },
        marriageToVeteranEndInfo: {
          path: 'household/marriage-to-veteran-end-info',
          title: content['household-veteran-marriage-end-info-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            !formData.marriedToVeteranAtTimeOfDeath,
          uiSchema: householdInformation.marriageToVeteranEndInfo.uiSchema,
          schema: householdInformation.marriageToVeteranEndInfo.schema,
        },
        legalStatusOfMarriage: {
          path: 'household/legal-status-of-marriage',
          title: content['household-legal-status-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.legalStatusOfMarriage.uiSchema,
          schema: householdInformation.legalStatusOfMarriage.schema,
        },
        marriageStatus: {
          path: 'household/marriage-status',
          title: content['household-marriage-status-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.marriageStatus.uiSchema,
          schema: householdInformation.marriageStatus.schema,
        },
        reasonForSeparation: {
          path: 'household/reason-for-separation',
          title: content['household-separation-reason-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            formData.livedContinuouslyWithVeteran === false,
          uiSchema: householdInformation.reasonForSeparation.uiSchema,
          schema: householdInformation.reasonForSeparation.schema,
        },
        separationDetails: {
          path: 'household/separation-details',
          title: content['household-separation-details-title'],
          depends: formData => {
            if (formData?.survivorsBenefitsForm2025VersionEnabled) {
              return (
                formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
                formData.livedContinuouslyWithVeteran === false &&
                formData?.separationDueToAssignedReasons &&
                formData?.separationDueToAssignedReasons !== 'MEDICAL_FINANCIAL'
              );
            }
            return (
              formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
              formData.livedContinuouslyWithVeteran === false &&
              formData?.separationDueToAssignedReasons
            );
          },
          uiSchema: householdInformation.separationDetails.uiSchema,
          schema: householdInformation.separationDetails.schema,
        },
        spouseMarriages: {
          path: 'household/spouse-marriage-question',
          title: content['household-spouse-marriages-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.spouseMarriages.uiSchema,
          schema: householdInformation.spouseMarriages.schema,
        },
        ...householdInformation.veteranMarriagesPages,
        ...householdInformation.previousMarriagesPages,
        remarriage: {
          path: 'household/remarriage',
          title: content['household-remarriage-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.remarriage.uiSchema,
          schema: householdInformation.remarriage.schema,
        },
        remarriageStatus: {
          path: 'household/remarriage-status',
          title: content['household-remarriage-status-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            formData.remarriedAfterVeteralDeath === true,
          uiSchema: householdInformation.remarriageStatus.uiSchema,
          schema: householdInformation.remarriageStatus.schema,
        },
        remarriageDetails: {
          path: 'household/remarriage-details',
          title: content['household-remarriage-details-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            formData.remarriedAfterVeteralDeath === true,
          uiSchema: householdInformation.remarriageDetails.uiSchema,
          schema: householdInformation.remarriageDetails.schema,
        },
        additionalMarriages: {
          path: 'household/additional-marriages',
          title: content['household-additional-marriages-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE' &&
            formData.remarriedAfterVeteralDeath === true,
          uiSchema: householdInformation.additionalMarriages.uiSchema,
          schema: householdInformation.additionalMarriages.schema,
        },
        veteranChildren: {
          path: 'household/children-of-veteran',
          title: content['household-veteran-children-title'],
          depends: formData =>
            formData.claimantRelationship === 'SURVIVING_SPOUSE',
          uiSchema: householdInformation.veteranChildren.uiSchema,
          schema: householdInformation.veteranChildren.schema,
        },
        dependentsCount: {
          path: 'household/dependents-count',
          title: content['household-dependents-count-title'],
          uiSchema: householdInformation.dependentsCount.uiSchema,
          schema: householdInformation.dependentsCount.schema,
        },
        ...householdInformation.dependentsPages,
        dependentsResidence: {
          path: 'household/dependents-residence',
          title: content['household-dependents-residence-title'],
          depends: formData => doAnyChildrenNotLiveWith(formData) === true,
          uiSchema: householdInformation.dependentsResidence.uiSchema,
          schema: householdInformation.dependentsResidence.schema,
        },
        dependentsAddress: {
          path: 'household/dependents-address',
          title: content['household-dependents-address-title'],
          depends: formData =>
            doAnyChildrenNotLiveWith(formData) === true &&
            formData?.childrenLiveTogetherButNotWithSpouse === true,
          uiSchema: householdInformation.dependentsAddress.uiSchema,
          schema: householdInformation.dependentsAddress.schema,
        },
        dependentsName: {
          path: 'household/dependents-custodian',
          title: content['household-dependents-custodian-name-title'],
          depends: formData =>
            doAnyChildrenNotLiveWith(formData) === true &&
            formData?.childrenLiveTogetherButNotWithSpouse === true,
          uiSchema: householdInformation.dependentsCustodian.uiSchema,
          schema: householdInformation.dependentsCustodian.schema,
        },
      },
    },
    // Claim Information
    claimInformation: {
      title: content['claim-information-chapter-title'],
      pages: {
        dicBenefits: {
          title: content['claim-information-dic-benefits-title'],
          path: 'claim-information/dic',
          depends: formData =>
            formData?.claims?.dic === true || formData?.claims?.DIC === true,
          uiSchema: claimInformation.dicBenefits.uiSchema,
          schema: claimInformation.dicBenefits.schema,
        },
        ...claimInformation.treatmentPages,
        nursingHome: {
          title: content['claim-information-nursing-home-title'],
          path: 'claim-information/nursing-home',
          uiSchema: claimInformation.nursingHome.uiSchema,
          schema: claimInformation.nursingHome.schema,
        },
      },
    },
    // Financial Information
    financialInformation: {
      title: content['financial-info-chapter-title'],
      pages: {
        incomeAndAssets: {
          title: content['financial-info-income-assets-title'],
          path: 'financial-information/income-and-assets',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.incomeAndAssets.uiSchema,
          schema: financialInformation.incomeAndAssets.schema,
        },
        totalAssets: {
          title: content['financial-info-total-assets-title'],
          path: 'financial-information/total-assets',
          depends: formData =>
            formData?.totalNetWorth === false &&
            formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.totalAssets.uiSchema,
          schema: financialInformation.totalAssets.schema,
        },
        transferredAssets: {
          title: content['financial-info-transferred-title'],
          path: 'financial-information/transferred-assets',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.transferredAssets.uiSchema,
          schema: financialInformation.transferredAssets.schema,
        },
        homeOwnership: {
          title: content['financial-info-home-ownership-title'],
          path: 'financial-information/homeownership',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.homeOwnership.uiSchema,
          schema: financialInformation.homeOwnership.schema,
        },
        landLotSize: {
          title: content['financial-info-land-lot-title'],
          path: 'financial-information/land-lot-size',
          depends: formData =>
            formData?.homeOwnership === true &&
            formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.landLotSize.uiSchema,
          schema: financialInformation.landLotSize.schema,
        },
        additionalLandValue: {
          title: content['financial-info-additional-land-title'],
          path: 'financial-information/additional-land-value',
          depends: formData =>
            formData?.homeAcreageMoreThanTwo === true &&
            formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.additionalLandValue.uiSchema,
          schema: financialInformation.additionalLandValue.schema,
        },
        marketableLand: {
          title: content['financial-info-marketable-land-title'],
          path: 'financial-information/marketable-land',
          depends: formData =>
            formData?.homeAcreageMoreThanTwo === true &&
            formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.marketableLand.uiSchema,
          schema: financialInformation.marketableLand.schema,
        },
        incomeSources: {
          title: content['financial-info-income-sources-title'],
          path: 'financial-information/income-sources',
          depends: formData => formData?.claims?.survivorsPension === true,
          uiSchema: financialInformation.incomeSources.uiSchema,
          schema: financialInformation.incomeSources.schema,
        },
        ...financialInformation.grossMonthlyIncomePages,
        ...financialInformation.careExpensesPages,
        ...financialInformation.medicalExpensesPages,
      },
    },
    // Additional Information
    additionalInformation: {
      title: content['additional-info-chapter-title'],
      pages: {
        directDeposit: {
          title: content['additional-info-deposit-title'],
          path: 'additional-information/direct-deposit',
          uiSchema: additionalInformation.directDeposit.uiSchema,
          schema: additionalInformation.directDeposit.schema,
        },
        directDepositAccount: {
          title: content['additional-info-account-title'],
          path: 'additional-information/direct-deposit/account',
          depends: additionalInformation.directDepositAccount.depends,
          uiSchema: additionalInformation.directDepositAccount.uiSchema,
          schema: additionalInformation.directDepositAccount.schema,
        },
        otherPaymentOptions: {
          title: content['additional-info-other-payment-title'],
          path: 'additional-information/other-payment-options',
          depends: additionalInformation.otherPaymentOptions.depends,
          uiSchema: additionalInformation.otherPaymentOptions.uiSchema,
          schema: additionalInformation.otherPaymentOptions.schema,
        },
        supportingDocuments: {
          title: content['additional-info-supporting-documents-title'],
          path: 'additional-information/supporting-documents',
          uiSchema: additionalInformation.supportingDocuments.uiSchema,
          schema: additionalInformation.supportingDocuments.schema,
        },
        uploadDocuments: {
          title: content['additional-info-upload-documents-title'],
          path: 'additional-information/upload-documents',
          depends: formData => formData?.['view:idpEnabled'] !== true,
          uiSchema: additionalInformation.uploadDocuments.uiSchema,
          schema: additionalInformation.uploadDocuments.schema,
          scrollAndFocusTarget,
        },
        uploadDocumentsWithCave: {
          title: 'Upload documents',
          path: 'additional-information/upload-documents-cave',
          depends: formData => formData?.['view:idpEnabled'] === true,
          uiSchema: caveUploadDocumentsPage.uiSchema,
          schema: caveUploadDocumentsPage.schema,
          CustomPageReview: caveUploadDocumentsPage.CustomPageReview,
        },
        confirmVeteranInfo: {
          title: 'Confirm Veteran information',
          path: 'additional-information/confirm-veteran-info',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            formData?.files?.some(f => f?.idpArtifacts) &&
            hasVeteranInfoConflicts(formData),
          uiSchema: caveConfirmVeteranInfoPage.uiSchema,
          schema: caveConfirmVeteranInfoPage.schema,
        },
        confirmMilitaryHistory: {
          title: 'Confirm military history',
          path: 'additional-information/confirm-military-history',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            formData?.files?.some(f => f?.idpArtifacts) &&
            hasMilitaryHistoryConflicts(formData),
          uiSchema: caveConfirmMilitaryHistoryPage.uiSchema,
          schema: caveConfirmMilitaryHistoryPage.schema,
        },
        artifactReview: {
          title: 'Review uploaded documents',
          path: 'additional-information/document-review',
          depends: formData =>
            formData?.['view:idpEnabled'] === true &&
            formData?.files?.some(f => f?.idpArtifacts),
          CustomPage: ArtifactReviewPage,
          CustomPageReview: null,
          uiSchema: {},
          schema: blankSchema,
        },
      },
    },
  },
};

export default applyForceDivWrapperToFormConfig(formConfig);
